import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ApiError,
  CreditLine,
  CreditWeight,
  OrgBilling,
  UsageGroupBy,
  UsageReport,
  UsageRow,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

function gb(n: number): string {
  return `${n.toLocaleString(undefined, {
    maximumFractionDigits: n < 10 ? 1 : 0,
  })} GB`;
}

function fmtDate(d: string | null): string {
  if (!d) return "-";
  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** A calendar-month bucket is "YYYY-MM"; a billing period bucket is its start
 *  date, which is already rendered as a range elsewhere in the row. */
function fmtBucket(row: UsageRow): string {
  if (!/^\d{4}-\d{2}$/.test(row.bucket)) return fmtDate(row.period_start);
  return new Date(`${row.bucket}-01T00:00:00`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Presets span whole calendar months and the end bound is exclusive, which
 *  matches both groupings: the period lens keeps any cycle overlapping the
 *  window, the month lens filters ledger timestamps below it. */
function monthsAgoRange(months: number): [string, string] {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - months, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return [iso(start), iso(end)];
}

function lastMonthRange(): [string, string] {
  const now = new Date();
  return [
    iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    iso(new Date(now.getFullYear(), now.getMonth(), 1)),
  ];
}

type SortKey = "bucket" | "organization" | "used" | "overage" | "pct";

export default function AdminUsage() {
  const me = useAuth((s) => s.user);
  const isOwner = me?.role === "owner";
  // Owners get the cross-tenant console. An admin gets the same report for
  // their own organization only, which the API pins regardless of the query.
  const canView = isOwner || me?.role === "admin";

  // The filters live in the URL so a view can be linked to and shared -- which
  // is how /admin/billing deep-links into one customer's history.
  const [sp, setSp] = useSearchParams();
  const orgFilter = isOwner ? (sp.get("organization_id") ?? "") : "";
  const groupBy = (sp.get("group_by") as UsageGroupBy) || "period";
  const actionFilter = sp.get("action_key") ?? "";
  const statusFilter = sp.get("status") ?? "";
  const start = sp.get("start") ?? "";
  const end = sp.get("end") ?? "";
  const overOnly = sp.get("over_only") === "1";

  const byPeriod = groupBy === "period";
  // Overage only exists against a whole period's allowance, so these two
  // controls are meaningless for calendar months or a single action.
  const overageAvailable = byPeriod && !actionFilter;

  const [report, setReport] = useState<UsageReport | null>(null);
  const [orgs, setOrgs] = useState<OrgBilling[]>([]);
  const [weights, setWeights] = useState<CreditWeight[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>("bucket");
  const [sortAsc, setSortAsc] = useState(false);

  const [openRow, setOpenRow] = useState<string | null>(null);
  const [lines, setLines] = useState<Record<string, CreditLine[]>>({});

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (orgFilter) p.set("organization_id", orgFilter);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    if (actionFilter) p.set("action_key", actionFilter);
    p.set("group_by", groupBy);
    if (byPeriod && statusFilter) p.set("status", statusFilter);
    if (overageAvailable && overOnly) p.set("over_only", "1");
    return p.toString();
  }, [
    orgFilter,
    start,
    end,
    actionFilter,
    groupBy,
    byPeriod,
    statusFilter,
    overageAvailable,
    overOnly,
  ]);

  const load = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      setReport(await api.get<UsageReport>(`/admin/billing/usage?${query}`));
      setOpenRow(null);
      setLines({});
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load credit usage");
    } finally {
      setBusy(false);
    }
  }, [query]);

  useEffect(() => {
    if (!canView) return;
    void load();
  }, [canView, load]);

  useEffect(() => {
    if (!isOwner) return;
    // Filter options are stable, so they load once rather than on every query.
    // Both endpoints are owner-only; an admin's activity list is built from
    // the report itself below, which needs no extra permission.
    void (async () => {
      try {
        const [orgList, weightList] = await Promise.all([
          api.get<OrgBilling[]>("/admin/billing/orgs"),
          api.get<CreditWeight[]>("/admin/credit-weights"),
        ]);
        setOrgs(orgList);
        setWeights(weightList);
      } catch {
        // Non-fatal: the table still works, the dropdowns are just empty.
      }
    })();
  }, [isOwner]);

  // `by_action` deliberately ignores the action filter, so using it as the
  // dropdown's source doesn't strip out the way back to the other activities.
  const activities = useMemo(
    () =>
      isOwner
        ? weights.map((w) => ({ key: w.action_key, label: w.label }))
        : (report?.by_action ?? []).map((a) => ({
            key: a.action_key,
            label: a.label,
          })),
    [isOwner, weights, report],
  );

  function setParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setSp(next, { replace: true });
  }

  function sortBy(key: SortKey) {
    if (key === sortKey) {
      setSortAsc((a) => !a);
    } else {
      setSortKey(key);
      setSortAsc(key === "organization");
    }
  }

  const rows = useMemo(() => {
    const list = [...(report?.rows ?? [])];
    const dir = sortAsc ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case "organization":
          return dir * a.organization_name.localeCompare(b.organization_name);
        case "used":
          return dir * (a.credits_used - b.credits_used);
        case "overage":
          return dir * ((a.overage_credits ?? 0) - (b.overage_credits ?? 0));
        case "pct":
          return dir * ((a.pct_used ?? -1) - (b.pct_used ?? -1));
        default:
          return (
            dir * a.bucket.localeCompare(b.bucket) ||
            a.organization_name.localeCompare(b.organization_name)
          );
      }
    });
    return list;
  }, [report, sortKey, sortAsc]);

  function rowKey(r: UsageRow): string {
    return `${r.organization_id}:${r.bucket}`;
  }

  async function toggleRow(r: UsageRow) {
    const key = rowKey(r);
    if (openRow === key) {
      setOpenRow(null);
      return;
    }
    setOpenRow(key);
    if (lines[key]) return;
    const p = new URLSearchParams({ organization_id: String(r.organization_id) });
    if (r.period_id != null) p.set("period_id", String(r.period_id));
    else {
      p.set("start", r.period_start);
      p.set("end", r.period_end);
    }
    try {
      const detail = await api.get<CreditLine[]>(
        `/admin/billing/usage/breakdown?${p.toString()}`,
      );
      setLines((m) => ({ ...m, [key]: detail }));
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load breakdown");
    }
  }

  async function exportCsv() {
    setErr(null);
    try {
      const blob = await api.blob(`/admin/billing/usage.csv?${query}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `credit-usage-${iso(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Export failed");
    }
  }

  if (!canView) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Billing</div>
            <h1>Credit usage</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          Credit usage is restricted to administrators.
        </div>
      </div>
    );
  }

  const totals = report?.totals;
  const activePreset =
    !start && !end
      ? "all"
      : start === monthsAgoRange(0)[0] && end === monthsAgoRange(0)[1]
        ? "this"
        : start === lastMonthRange()[0] && end === lastMonthRange()[1]
          ? "last"
          : start === monthsAgoRange(2)[0] && end === monthsAgoRange(2)[1]
            ? "3mo"
            : start === monthsAgoRange(5)[0] && end === monthsAgoRange(5)[1]
              ? "6mo"
              : "custom";

  const colCount =
    (isOwner ? 1 : 0) + // customer
    6 + // period, tier, credits used, storage, invoice-or-events, breakdown button
    (byPeriod && !actionFilter ? 4 : 0); // allowance, consumed, overage, projection

  // Rows are one org's periods, so a count of over-allowance ORGS says nothing
  // an admin can act on. Months do.
  const periodsOver = rows.filter((r) => r.overage_credits).length;

  return (
    <div className="admin admin--page-scroll">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Billing</div>
          <h1>Credit usage</h1>
          <p className="admin__lead">
            {isOwner
              ? "What every customer has consumed, this month and every month before it. Open periods update live, so a customer heading for overage shows up here well before their invoice drafts."
              : "What your organization has consumed, this month and every month before it. The current period updates live, so you can see any overage building well before it reaches an invoice."}
          </p>
        </div>
        <div className="admin__head-actions">
          <button type="button" className="btn" onClick={() => void exportCsv()}>
            Export CSV
          </button>
          <button type="button" className="btn" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}

      {totals && (
        <div className="billing-stats">
          <div className="billing-stat">
            <span className="billing-stat__label">Credits used</span>
            <span className="billing-stat__value">
              {totals.credits_used.toLocaleString()}
            </span>
            <span className="billing-stat__hint">
              {isOwner
                ? `${totals.organizations} customer${
                    totals.organizations === 1 ? "" : "s"
                  } over ${totals.rows} month${totals.rows === 1 ? "" : "s"}`
                : `across ${totals.rows} month${totals.rows === 1 ? "" : "s"}`}
            </span>
          </div>
          <div className="billing-stat">
            <span className="billing-stat__label">Overage</span>
            <span className="billing-stat__value">
              {totals.allowance_comparable
                ? totals.overage_credits.toLocaleString()
                : "-"}
            </span>
            <span className="billing-stat__hint">
              {totals.allowance_comparable
                ? "credits past allowance" +
                  (totals.storage_overage_gb > 0
                    ? `, plus ${gb(totals.storage_overage_gb)} of storage`
                    : "")
                : "not defined for calendar months"}
            </span>
          </div>
          {isOwner ? (
            <>
              <div className="billing-stat">
                <span className="billing-stat__label">Over allowance</span>
                <span className="billing-stat__value">
                  {totals.allowance_comparable ? totals.orgs_over : "-"}
                </span>
                <span className="billing-stat__hint">
                  customers billed for extra credits
                </span>
              </div>
              <div className="billing-stat">
                <span className="billing-stat__label">Approaching limit</span>
                <span className="billing-stat__value">
                  {totals.allowance_comparable ? totals.orgs_warning : "-"}
                </span>
                <span className="billing-stat__hint">
                  past their alert threshold
                </span>
              </div>
              {byPeriod && (
                <div className="billing-stat">
                  <span className="billing-stat__label">Tracking over</span>
                  <span className="billing-stat__value">
                    {totals.orgs_projected_over}
                  </span>
                  <span className="billing-stat__hint">
                    on course to exceed their allowance
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="billing-stat">
              <span className="billing-stat__label">Months over allowance</span>
              <span className="billing-stat__value">
                {totals.allowance_comparable ? periodsOver : "-"}
              </span>
              <span className="billing-stat__hint">
                {totals.allowance_comparable
                  ? "billing periods that ran past the plan"
                  : "not defined for calendar months"}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="admin-card">
        <div className="billing-filters">
          <div className="admin-card__field">
            <label htmlFor="uf-group">Group by</label>
            <div className="seg-toggle" id="uf-group">
              <button
                type="button"
                className={`seg-toggle__btn${byPeriod ? " seg-toggle__btn--on" : ""}`}
                onClick={() => setParams({ group_by: null })}
                title="Each customer's own billing cycle - what they get invoiced on"
              >
                Billing periods
              </button>
              <button
                type="button"
                className={`seg-toggle__btn${byPeriod ? "" : " seg-toggle__btn--on"}`}
                onClick={() =>
                  setParams({ group_by: "month", status: null, over_only: null })
                }
                title="Calendar months - comparable across customers, but no allowance to compare against"
              >
                Calendar months
              </button>
            </div>
          </div>

          {isOwner && (
            <div className="admin-card__field">
              <label htmlFor="uf-org">Customer</label>
              <select
                id="uf-org"
                value={orgFilter}
                onChange={(e) => setParams({ organization_id: e.target.value })}
              >
                <option value="">All customers</option>
                {orgs.map((o) => (
                  <option key={o.organization_id} value={o.organization_id}>
                    {o.organization_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="admin-card__field">
            <label htmlFor="uf-action">Activity</label>
            <select
              id="uf-action"
              value={actionFilter}
              onChange={(e) => setParams({ action_key: e.target.value })}
            >
              <option value="">All activities</option>
              {activities.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>

          {byPeriod && (
            <div className="admin-card__field">
              <label htmlFor="uf-status">Period</label>
              <select
                id="uf-status"
                value={statusFilter}
                onChange={(e) => setParams({ status: e.target.value })}
              >
                <option value="">Open and closed</option>
                <option value="open">Current (open) only</option>
                <option value="closed">Closed only</option>
              </select>
            </div>
          )}

          <div className="admin-card__field">
            <label htmlFor="uf-start">From</label>
            <input
              id="uf-start"
              type="date"
              value={start}
              onChange={(e) => setParams({ start: e.target.value })}
            />
          </div>
          <div className="admin-card__field">
            <label htmlFor="uf-end">To</label>
            <input
              id="uf-end"
              type="date"
              value={end}
              onChange={(e) => setParams({ end: e.target.value })}
            />
          </div>

          <button
            type="button"
            className="btn"
            onClick={() => setSp(new URLSearchParams(), { replace: true })}
          >
            Clear
          </button>
        </div>

        <div className="usage-quick">
          {(
            [
              ["all", "All time", null],
              ["this", "This month", monthsAgoRange(0)],
              ["last", "Last month", lastMonthRange()],
              ["3mo", "Last 3 months", monthsAgoRange(2)],
              ["6mo", "Last 6 months", monthsAgoRange(5)],
            ] as [string, string, [string, string] | null][]
          ).map(([key, label, range]) => (
            <button
              key={key}
              type="button"
              className={`chip chip--toggle${activePreset === key ? " chip--on" : ""}`}
              onClick={() =>
                setParams({
                  start: range ? range[0] : null,
                  end: range ? range[1] : null,
                })
              }
            >
              {label}
            </button>
          ))}
          {overageAvailable && (
            <button
              type="button"
              className={`chip chip--toggle${overOnly ? " chip--on" : ""}`}
              onClick={() => setParams({ over_only: overOnly ? null : "1" })}
              title={
                isOwner
                  ? "Only customers past their allowance, or on course to be"
                  : "Only periods past your allowance, or on course to be"
              }
            >
              Over allowance only
            </button>
          )}
        </div>

        {!byPeriod && (
          <p className="admin-card__blurb">
            {isOwner
              ? "Calendar months are comparable across customers, but they straddle billing anniversaries, so there is no allowance to measure overage against. Switch to billing periods for that."
              : "Calendar months straddle your billing anniversary, so there is no allowance to measure overage against. Switch to billing periods for that."}
          </p>
        )}
        {byPeriod && actionFilter && (
          <p className="admin-card__blurb">
            Showing one activity only, so these credits are a subset of each
            period. Allowance and overage are hidden because a subset cannot be
            compared against the whole period's allowance.
          </p>
        )}
      </div>

      <div className="admin__scroll">
        <table className="admin-table">
          <thead>
            <tr>
              {isOwner && (
                <th>
                  <button
                    type="button"
                    className="btn btn--link"
                    onClick={() => sortBy("organization")}
                  >
                    Customer
                  </button>
                </th>
              )}
              <th>
                <button
                  type="button"
                  className="btn btn--link"
                  onClick={() => sortBy("bucket")}
                >
                  {byPeriod ? "Billing period" : "Month"}
                </button>
              </th>
              <th>Tier</th>
              {byPeriod && !actionFilter && <th>Allowance</th>}
              <th>
                <button
                  type="button"
                  className="btn btn--link"
                  onClick={() => sortBy("used")}
                >
                  Credits used
                </button>
              </th>
              {byPeriod && !actionFilter && (
                <>
                  <th>
                    <button
                      type="button"
                      className="btn btn--link"
                      onClick={() => sortBy("pct")}
                    >
                      Consumed
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="btn btn--link"
                      onClick={() => sortBy("overage")}
                    >
                      Overage
                    </button>
                  </th>
                  <th>Projection</th>
                </>
              )}
              <th>Storage</th>
              {!byPeriod && <th>Events</th>}
              {byPeriod && <th>Invoice</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr>
                <td colSpan={colCount} className="muted">
                  Loading...
                </td>
              </tr>
            )}
            {!busy && rows.length === 0 && (
              <tr>
                <td colSpan={colCount} className="muted">
                  No credit usage matches these filters.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const key = rowKey(r);
              const expanded = openRow === key;
              const pct = r.pct_used ?? 0;
              const tone =
                r.alert_state === "over"
                  ? "danger"
                  : r.alert_state === "warning"
                    ? "warn"
                    : "ok";
              return [
                <tr
                  key={key}
                  className={r.is_active ? "" : "admin-table__row--inactive"}
                >
                  {isOwner && (
                    <td>
                      <div className="admin-table__name">
                        {r.organization_name}
                        {!r.is_active && (
                          <span className="admin-table__chip">deactivated</span>
                        )}
                      </div>
                    </td>
                  )}
                  <td className="billing-num">
                    {byPeriod ? fmtDate(r.period_start) : fmtBucket(r)}
                    {byPeriod && (
                      <div className="muted admin-table__mono">
                        to {fmtDate(r.period_end)}
                        {r.status === "open" && " (current)"}
                      </div>
                    )}
                  </td>
                  <td>{r.tier_name ?? <span className="muted">-</span>}</td>
                  {byPeriod && !actionFilter && (
                    <td className="billing-num">
                      {r.included_credits?.toLocaleString() ?? "-"}
                    </td>
                  )}
                  <td className="billing-num">
                    <strong>{r.credits_used.toLocaleString()}</strong>
                    {r.events != null && (
                      <div className="muted admin-table__mono">
                        {r.events.toLocaleString()} action
                        {r.events === 1 ? "" : "s"}
                      </div>
                    )}
                  </td>
                  {byPeriod && !actionFilter && (
                    <>
                      <td>
                        <div className="progress-cell">
                          <div className="progress">
                            <div
                              className={`progress__bar credits__bar-fill--${tone}`}
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                          <span className="progress-cell__pct">
                            {pct.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="billing-num">
                        {r.overage_credits ? (
                          <>
                            <span className="billing-num--over">
                              {r.overage_credits.toLocaleString()}
                            </span>
                            <div className="muted admin-table__mono">
                              credits over
                            </div>
                          </>
                        ) : (
                          <span className="muted">-</span>
                        )}
                      </td>
                      <td className="billing-num">
                        {r.status !== "open" ? (
                          <span className="muted">-</span>
                        ) : r.credits_used === 0 ? (
                          <span className="muted">no usage yet</span>
                        ) : r.projected_credits == null ? (
                          <span
                            className="muted"
                            title={`Only ${r.days_elapsed} day(s) into the period - too early for a run rate to mean anything`}
                          >
                            too early
                          </span>
                        ) : (
                          <>
                            {r.projected_credits.toLocaleString()}
                            <div className="muted admin-table__mono">
                              {r.projected_overage_credits
                                ? `${r.projected_overage_credits.toLocaleString()} over at this rate`
                                : `day ${r.days_elapsed} of ${r.days_total}`}
                            </div>
                          </>
                        )}
                      </td>
                    </>
                  )}
                  <td className="billing-num">
                    {r.peak_storage_gb == null ? (
                      <span className="muted">-</span>
                    ) : byPeriod &&
                      !r.included_storage_gb &&
                      !r.peak_storage_gb ? (
                      // Periods that closed before storage was metered carry
                      // zeros; "0 GB of 0 GB" would read as a broken meter.
                      <span
                        className="muted"
                        title="This period closed before storage was metered."
                      >
                        not metered
                      </span>
                    ) : (
                      <>
                        {gb(r.peak_storage_gb)}
                        <div className="muted admin-table__mono">
                          {r.storage_overage_gb
                            ? `${gb(r.storage_overage_gb)} over ${gb(
                                r.included_storage_gb ?? 0,
                              )}`
                            : r.included_storage_gb != null
                              ? `peak of ${gb(r.included_storage_gb)}`
                              : "peak this month"}
                        </div>
                      </>
                    )}
                  </td>
                  {!byPeriod && (
                    <td className="billing-num">
                      {r.events?.toLocaleString() ?? "-"}
                    </td>
                  )}
                  {byPeriod && (
                    <td>
                      {r.invoice_number ? (
                        <>
                          <div className="admin-table__mono">
                            {r.invoice_number}
                          </div>
                          <span
                            className={`invoice-status invoice-status--${r.invoice_status}`}
                          >
                            {r.invoice_status}
                          </span>
                        </>
                      ) : (
                        <span className="muted">
                          {r.status === "open" ? "in progress" : "none"}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="admin-table__actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => void toggleRow(r)}
                    >
                      {expanded ? "Hide" : "Breakdown"}
                    </button>
                  </td>
                </tr>,
                expanded && (
                  <tr key={`${key}:detail`} className="usage-detail">
                    <td colSpan={colCount}>
                      {lines[key] == null ? (
                        <span className="muted">Loading breakdown...</span>
                      ) : lines[key].length === 0 ? (
                        <span className="muted">
                          No credit activity recorded in this window.
                        </span>
                      ) : (
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Activity</th>
                              <th>Volume</th>
                              <th>Times</th>
                              <th>Credits</th>
                              <th>Share</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lines[key].map((l) => {
                              const share =
                                r.credits_used > 0
                                  ? (l.credits / r.credits_used) * 100
                                  : 0;
                              return (
                                <tr key={l.action_key}>
                                  <td>
                                    <div className="admin-table__name">
                                      {l.label}
                                    </div>
                                  </td>
                                  <td className="billing-num">
                                    {Number.isInteger(l.quantity)
                                      ? l.quantity.toLocaleString()
                                      : l.quantity.toFixed(1)}{" "}
                                    {l.unit}
                                    {l.quantity === 1 ? "" : "s"}
                                  </td>
                                  <td className="billing-num">
                                    {l.events.toLocaleString()}
                                  </td>
                                  <td className="billing-num">
                                    {l.credits.toLocaleString()}
                                  </td>
                                  <td>
                                    <div className="progress-cell">
                                      <div className="progress">
                                        <div
                                          className="progress__bar"
                                          style={{ width: `${share}%` }}
                                        />
                                      </div>
                                      <span className="progress-cell__pct">
                                        {share.toFixed(0)}%
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      {report && report.by_action.length > 0 && (
        <div className="admin-card">
          <div className="admin-card__head">
            <div>
              <h2 className="admin-card__title">Where the credits went</h2>
              <p className="admin-card__blurb">
                Every activity across the {isOwner ? "customers and dates" : "dates"}{" "}
                selected above, regardless of which single activity the table is
                filtered to. Click one to filter the table by it.
              </p>
            </div>
          </div>
          <div className="admin__scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Activity</th>
                  <th>Volume</th>
                  <th>Times</th>
                  {isOwner && <th>Customers</th>}
                  <th>Credits</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {report.by_action.map((a) => {
                  const totalCredits = report.by_action.reduce(
                    (s, x) => s + x.credits,
                    0,
                  );
                  const share =
                    totalCredits > 0 ? (a.credits / totalCredits) * 100 : 0;
                  return (
                    <tr
                      key={a.action_key}
                      className={
                        actionFilter === a.action_key
                          ? "admin-table__row--dirty"
                          : ""
                      }
                    >
                      <td>
                        <button
                          type="button"
                          className="btn btn--link admin-table__name"
                          onClick={() =>
                            setParams({
                              action_key:
                                actionFilter === a.action_key
                                  ? null
                                  : a.action_key,
                            })
                          }
                        >
                          {a.label}
                        </button>
                      </td>
                      <td className="billing-num">
                        {Number.isInteger(a.quantity)
                          ? a.quantity.toLocaleString()
                          : a.quantity.toFixed(1)}{" "}
                        {a.unit}
                        {a.quantity === 1 ? "" : "s"}
                      </td>
                      <td className="billing-num">
                        {a.events.toLocaleString()}
                      </td>
                      {isOwner && (
                        <td className="billing-num">{a.organizations}</td>
                      )}
                      <td className="billing-num">
                        {a.credits.toLocaleString()}
                      </td>
                      <td>
                        <div className="progress-cell">
                          <div className="progress">
                            <div
                              className="progress__bar"
                              style={{ width: `${share}%` }}
                            />
                          </div>
                          <span className="progress-cell__pct">
                            {share.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="admin__lead muted">
        Looking for closed periods rather than live consumption?{" "}
        {isOwner ? (
          <>
            See <Link to="/admin/invoices">usage statements</Link>, or{" "}
            <Link to="/admin/billing">billing settings</Link> to change a
            customer's tier or allowance.
          </>
        ) : (
          <>
            See <Link to="/credits">your plan and statements</Link>.
          </>
        )}
      </p>
    </div>
  );
}
